import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle, Trophy } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import api from '../lib/api';
import { Lesson, QuizQuestion } from '../types';
import { toast } from 'sonner';
import Confetti from 'react-confetti';

export default function LessonPage() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showQuiz, setShowQuiz] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, number>>({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const { data: lessonData, isLoading } = useQuery({
    queryKey: ['lesson', lessonId],
    queryFn: async () => {
      const response = await api.get(`/education/lessons/${lessonId}`);
      return response.data.data.lesson as Lesson;
    },
  });

  const completeMutation = useMutation({
    mutationFn: () => api.post(`/education/lessons/${lessonId}/complete`),
    onSuccess: (response) => {
      const data = response.data.data;
      queryClient.invalidateQueries({ queryKey: ['lesson', lessonId] });
      queryClient.invalidateQueries({ queryKey: ['education-progress'] });
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);
      toast.success('Lesson completed! 🎉', { description: `+${data.xpEarned} XP earned` });
    },
  });

  const lesson = lessonData;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Lesson not found</h2>
          <button onClick={() => navigate(-1)} className="text-primary-600 hover:underline">Go Back</button>
        </div>
      </div>
    );
  }

  const handleAnswerSelect = (questionId: string, answerIndex: number) => {
    setSelectedAnswers(prev => ({ ...prev, [questionId]: answerIndex }));
  };

  const handleQuizSubmit = () => {
    if (!lesson.quiz) return;
    const questions = lesson.quiz.questions;
    let correct = 0;
    questions.forEach(q => {
      if (selectedAnswers[q.id] === q.correctAnswer) correct++;
    });
    const score = (correct / questions.length) * 100;
    setQuizSubmitted(true);
    if (score >= 70) {
      completeMutation.mutate();
    } else {
      toast.error('Score too low', { description: `You need 70% to pass. You scored ${score.toFixed(0)}%` });
    }
  };

  const handleComplete = () => {
    if (lesson.hasQuiz && !lesson.isCompleted) {
      setShowQuiz(true);
    } else {
      completeMutation.mutate();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      {showConfetti && <Confetti recycle={false} numberOfPieces={500} />}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Course
          </button>
          {lesson.isCompleted && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center">
              <CheckCircle className="w-5 h-5 text-blue-900 mr-2" />
              <span className="text-blue-900 font-medium">You've completed this lesson</span>
            </div>
          )}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">{lesson.title}</h1>
          <div className="prose prose-lg max-w-none prose-headings:text-blue-900 prose-p:text-blue-900 prose-li:text-blue-900 prose-strong:text-blue-900 prose-em:text-blue-900 prose-a:text-blue-900 prose-blockquote:text-blue-900 prose-code:text-blue-900 [&_*]:text-blue-900">
            <ReactMarkdown>{lesson.content}</ReactMarkdown>
          </div>
          {lesson.videoUrl && (
            <div className="mt-8">
              <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
                <iframe src={lesson.videoUrl} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            </div>
          )}
        </motion.div>

        {lesson.hasQuiz && showQuiz && lesson.quiz && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className="flex items-center mb-6">
              <Trophy className="w-8 h-8 text-blue-900 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Knowledge Check</h2>
            </div>
            <div className="space-y-6">
              {lesson.quiz.questions.map((question: QuizQuestion, index: number) => (
                <div key={question.id} className="border-b pb-6 last:border-0">
                  <p className="font-semibold text-gray-900 mb-4">{index + 1}. {question.question}</p>
                  <div className="space-y-2">
                    {question.options.map((option, optionIndex) => {
                      const isSelected = selectedAnswers[question.id] === optionIndex;
                      const isCorrect = optionIndex === question.correctAnswer;
                      const showResult = quizSubmitted;
                      return (
                        <button
                          key={optionIndex}
                          onClick={() => !quizSubmitted && handleAnswerSelect(question.id, optionIndex)}
                          disabled={quizSubmitted}
                          className={`w-full text-left p-4 rounded-lg border-2 transition ${
                            showResult ? isCorrect ? 'border-green-500 bg-green-50' : isSelected ? 'border-red-500 bg-red-50' : 'border-gray-200'
                            : isSelected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${showResult && isCorrect ? 'bg-green-500 border-green-500' : isSelected ? 'border-primary-500' : 'border-gray-300'}`}>
                              {showResult && isCorrect && <CheckCircle className="w-4 h-4 text-white" />}
                              {isSelected && !showResult && <div className="w-3 h-3 bg-primary-500 rounded-full"></div>}
                            </div>
                            <span className={showResult && isCorrect ? 'font-medium' : ''}>{option}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {quizSubmitted && question.explanation && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-700">{question.explanation}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {!quizSubmitted ? (
              <button onClick={handleQuizSubmit} disabled={Object.keys(selectedAnswers).length < lesson.quiz.questions.length} className="btn btn-primary w-full mt-6">Submit Quiz</button>
            ) : (
              <button onClick={() => { setShowQuiz(false); setQuizSubmitted(false); setSelectedAnswers({}); }} className="btn btn-secondary w-full mt-6">Retake Quiz</button>
            )}
          </motion.div>
        )}

        {!lesson.isCompleted && !showQuiz && (
          <button onClick={handleComplete} className="btn btn-primary w-full">Mark as Complete</button>
        )}
      </div>
    </div>
  );
}
